import React, { useState, useCallback, useMemo } from 'react'
import Grid from '@material-ui/core/Grid'
import PropTypes from 'prop-types'
import SydButton from '../../../../../../commonDesign/Button'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../../commonDesign/SydInput'
import { componentTypeRegistry, getDomains, getTypesByDomain } from '../../../../../../organisms/ComponentBlueprint/componentTypeRegistry'

function AddComponentForm ({ onCancel, onComplete }) {
  const [componentCode, setComponentCode] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [componentType, setComponentType] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState(null)

  const domains = useMemo(() => getDomains(), [])
  const typesByDomain = useMemo(() => getTypesByDomain(), [])

  const isValid = componentCode.trim() &&
    displayName.trim() &&
    componentType &&
    /^[a-z0-9-]+$/.test(componentCode.trim())

  const handleCreate = useCallback(() => {
    if (!isValid) return

    const codeValue = componentCode.trim()
    if (!/^[a-z0-9-]+$/.test(codeValue)) {
      setError('Component code must be lowercase letters, numbers, and hyphens only')
      return
    }

    setError(null)

    const typeEntry = componentTypeRegistry[componentType]
    const defaultConfig = typeof typeEntry?.defaultConfig === 'function'
      ? typeEntry.defaultConfig()
      : (typeEntry?.defaultConfig ?? {})

    const newComponent = {
      componentType,
      displayName: displayName.trim(),
      description: description.trim() || null,
      config: defaultConfig,
      styleVariant: 'default',
      styleVariantParams: {}
    }

    onComplete({ code: codeValue, data: newComponent })
  }, [componentCode, displayName, componentType, description, isValid, onComplete])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <SydLabel required label='Component Code' helperText='Lowercase, numbers, hyphens (e.g., task-list)'>
          <SydInput
            value={componentCode}
            onChange={(e) => setComponentCode(e.target.value)}
            placeholder='task-list'
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12} md={6}>
        <SydLabel required label='Display Name' helperText='Human-readable name'>
          <SydInput
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder='Task List'
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12}>
        <SydLabel required label='Component Type'>
          <select
            value={componentType}
            onChange={(e) => setComponentType(e.target.value)}
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid var(--color-neutral-300)' }}
          >
            <option value=''>Select a component type...</option>
            {domains.map(d => (
              <optgroup key={d.domain} label={d.displayName}>
                {(typesByDomain[d.domain] || []).map(t => (
                  <option key={t.code} value={t.code}>{t.displayName}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </SydLabel>
      </Grid>

      <Grid item xs={12}>
        <SydLabel label='Description' helperText='Optional description of this component'>
          <SydInput
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            placeholder='A brief description of this component'
          />
        </SydLabel>
      </Grid>

      <Grid item xs={12}>
        {error ? <div style={{ color: 'red', marginBottom: '8px' }}>{error}</div> : null}
        <SydModalActions>
          <SydButton variant='ghost' size='md' onClick={onCancel}>
            Cancel
          </SydButton>
          <SydButton
            disabled={!isValid}
            variant='primary'
            size='md'
            onClick={handleCreate}
          >
            Add Component
          </SydButton>
        </SydModalActions>
      </Grid>
    </Grid>
  )
}

AddComponentForm.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default AddComponentForm
